import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from './button';

/**
 * Los tres estados que toda vista de datos debe cubrir. No son adornos:
 * una lista vacía sin explicación se interpreta como una avería, y un error
 * silencioso hace que el usuario reintente sin saber qué pasa.
 */

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-surface-3', className)} aria-hidden />;
}

export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} role="status" aria-label="Cargando">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-11 w-full" />
      ))}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}
    >
      {Icon && (
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-surface-2 text-fg-subtle">
          <Icon className="size-6" />
        </div>
      )}
      <p className="font-medium text-fg">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-fg-muted">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'No se han podido cargar los datos',
  description,
  onRetry,
  className,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border border-danger/30 bg-danger-soft/40 px-6 py-10 text-center',
        className,
      )}
    >
      <AlertTriangle className="mb-3 size-6 text-danger" />
      <p className="font-medium text-fg">{title}</p>
      {description && <p className="mt-1 max-w-md text-sm text-fg-muted">{description}</p>}
      {onRetry && (
        <Button variant="secondary" size="sm" className="mt-5" onClick={onRetry}>
          <RefreshCw />
          Reintentar
        </Button>
      )}
    </div>
  );
}
